import {
  ApartmentOutlined,
  AppstoreOutlined,
  DownOutlined,
  HourglassOutlined,
  ReloadOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Row,
  Skeleton,
  Space,
  Statistic,
  Typography,
} from 'antd';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchRecentErrors } from '@/api/endpoints/stats';
import RecentErrorsTable from '@/components/stats/RecentErrorsTable';
import { useStatsSummary } from '@/hooks/useStatsSummary';
import type { RecentError, StatsSummary } from '@/types/stats';
import { extractAxiosMessage } from '@/utils/axiosError';

const RECENT_ERRORS_LIMIT = 50;

export default function DashboardPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { status, data, error, refresh } = useStatsSummary();

  const [errorsOpen, setErrorsOpen] = useState(false);
  const [errors, setErrors] = useState<readonly RecentError[] | null>(null);
  const [errorsLoading, setErrorsLoading] = useState(false);

  const loadErrors = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const res = await fetchRecentErrors(RECENT_ERRORS_LIMIT);
      setErrors(res.items);
    } catch (err) {
      message.error(extractAxiosMessage(err) ?? t('dashboard.errors.loadError'));
      setErrors([]);
    } finally {
      setErrorsLoading(false);
    }
  }, [message, t]);

  const toggleErrors = useCallback(() => {
    const next = !errorsOpen;
    setErrorsOpen(next);
    // Lazy-load on first open; cached afterwards until a refresh clears it.
    if (next && errors === null) {
      void loadErrors();
    }
  }, [errorsOpen, errors, loadErrors]);

  const handleRefresh = useCallback(() => {
    refresh();
    setErrors(null);
    if (errorsOpen) {
      void loadErrors();
    }
  }, [refresh, errorsOpen, loadErrors]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('dashboard.title')}
        </Typography.Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={status === 'loading'}
        >
          {t('dashboard.refresh')}
        </Button>
      </Space>

      {status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={t('dashboard.loadError')}
          description={error ?? undefined}
        />
      )}

      {status === 'loading' && !data && <Skeleton active paragraph={{ rows: 4 }} />}

      {data && (
        <SummaryGrid
          data={data}
          errorsOpen={errorsOpen}
          onErrorsClick={toggleErrors}
        />
      )}

      {errorsOpen && (
        <Card title={t('dashboard.errors.title')} size="small">
          <RecentErrorsTable items={errors ?? []} loading={errorsLoading} />
        </Card>
      )}
    </Space>
  );
}

interface SummaryGridProps {
  readonly data: StatsSummary;
  readonly errorsOpen: boolean;
  readonly onErrorsClick: () => void;
}

function SummaryGrid({ data, errorsOpen, onErrorsClick }: SummaryGridProps) {
  const { t } = useTranslation();
  const recompute = data.recompute;
  return (
    <Row gutter={[16, 16]}>
      <SummaryCard
        title={t('dashboard.cards.routes')}
        value={data.routes_total}
        icon={<ApartmentOutlined />}
        suffix={
          data.routes_stale > 0 ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {t('dashboard.cards.routesStale', { count: data.routes_stale })}
            </Typography.Text>
          ) : undefined
        }
      />
      <SummaryCard
        title={t('dashboard.cards.barriers')}
        value={data.barriers_total}
        icon={<AppstoreOutlined />}
      />
      <SummaryCard
        title={t('dashboard.cards.queue')}
        value={data.routes_stale}
        icon={<HourglassOutlined />}
        suffix={
          recompute.status === 'running' ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {t('dashboard.cards.recomputeRunning', {
                processed: recompute.processed,
                total: recompute.total,
              })}
            </Typography.Text>
          ) : recompute.failed > 0 ? (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              {t('dashboard.cards.queueFailed', { count: recompute.failed })}
            </Typography.Text>
          ) : undefined
        }
      />
      <SummaryCard
        title={t('dashboard.cards.apiCalls24h')}
        value={data.api_calls_24h}
        icon={<ReloadOutlined />}
        suffix={
          data.api_errors_24h > 0 ? (
            <Typography.Link
              onClick={onErrorsClick}
              style={{ fontSize: 12 }}
              title={t('dashboard.errors.hint')}
            >
              {t('dashboard.cards.apiErrors24h', { count: data.api_errors_24h })}{' '}
              {errorsOpen ? <UpOutlined /> : <DownOutlined />}
            </Typography.Link>
          ) : undefined
        }
      />
    </Row>
  );
}

interface SummaryCardProps {
  readonly title: string;
  readonly value: number;
  readonly icon: ReactNode;
  readonly suffix?: ReactNode;
}

function SummaryCard({ title, value, icon, suffix }: SummaryCardProps) {
  return (
    <Col xs={24} sm={12} lg={6}>
      <Card>
        <Statistic title={title} value={value} prefix={icon} />
        {suffix && <div style={{ marginTop: 4 }}>{suffix}</div>}
      </Card>
    </Col>
  );
}
